import { useState, useCallback, useMemo, type ReactNode } from 'react'
import { App } from 'antd'
import { v4 as uuidv4 } from 'uuid'
import { useMindMapContext } from '../../../context/mindmap'
import { FigmaExportModal } from './FigmaExportModal'
import { StoryboardModal } from './StoryboardModal'
import { StoryboardConfirmModal } from './StoryboardConfirmModal'
import { UpgradePromptModal } from '../../UpgradePromptModal'
import { AIService } from '../../../services/ai/aiService'
import { ProfileService } from '../../../services/profile/profileService'
import { useFeatureAccessContext } from '../../../context/featureAccess'
import type { MindMapNode, MindMapEdge } from '@bandos/shared'
import type { StoryboardNodeData, VentureNodeData } from '../../../types/mindmap-components'
import { IdeaActionsContext, type IdeaActionsContextValue } from './IdeaActionsContextDef'

interface IdeaActionsProviderProps {
  children: ReactNode
}

export function IdeaActionsProvider({ children }: IdeaActionsProviderProps) {
  const { nodes, edges, mindmapId, setNodes, setEdges, setHasUnsavedChanges } = useMindMapContext()
  const { notification: antdNotification } = App.useApp()

  // Figma modal state
  const [figmaModalOpen, setFigmaModalOpen] = useState(false)
  const [figmaThreadNodes, setFigmaThreadNodes] = useState<MindMapNode[]>([])
  const [figmaThreadEdges, setFigmaThreadEdges] = useState<MindMapEdge[]>([])

  // Storyboard confirmation modal state (new generation)
  const [storyboardConfirmModalOpen, setStoryboardConfirmModalOpen] = useState(false)

  // Storyboard view modal state (existing storyboards)
  const [storyboardModalOpen, setStoryboardModalOpen] = useState(false)
  const [storyboardIdeaLabel, setStoryboardIdeaLabel] = useState('')
  const [storyboardIdeaNodeId, setStoryboardIdeaNodeId] = useState('')
  const [storyboardExistingUrls, setStoryboardExistingUrls] = useState<string[] | undefined>(
    undefined
  )
  const [storyboardExistingCaptions, setStoryboardExistingCaptions] = useState<
    string[] | undefined
  >(undefined)
  const [storyboardExistingTitle, setStoryboardExistingTitle] = useState<string | undefined>(
    undefined
  )

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('Unlimited storyboards')

  // Feature access for tier-based limits
  const { checkFeature } = useFeatureAccessContext()

  // Check if an idea's thread connects to a venture with audience context for storyboard generation
  const hasVentureAudienceForIdea = useCallback(
    (ideaNodeId: string): boolean => {
      const visited = new Set<string>()
      const queue = [ideaNodeId]

      while (queue.length > 0) {
        const currentId = queue.shift()
        if (currentId === undefined || visited.has(currentId)) continue
        visited.add(currentId)

        const currentNode = nodes.find((n) => n.id === currentId)
        if (currentNode?.data.category === 'venture') {
          const ventureData = currentNode.data as VentureNodeData
          const goals = ventureData.goals?.trim() ?? ''
          const businessModel = ventureData.businessModelAndValueProp?.trim() ?? ''
          return Boolean(goals) || Boolean(businessModel)
        }

        // Traverse up to ancestors
        const parentEdges = edges.filter((e) => e.target === currentId)
        for (const edge of parentEdges) {
          if (!visited.has(edge.source)) {
            queue.push(edge.source)
          }
        }
      }

      return false
    },
    [nodes, edges]
  )

  // Gather the thread: the idea, its ancestors, and any child ideas
  const gatherThreadNodes = useCallback(
    (ideaNodeId: string): { nodes: MindMapNode[]; edges: MindMapEdge[] } => {
      const threadNodeIds = new Set<string>()
      const threadEdges: MindMapEdge[] = []

      // Start with the idea node
      threadNodeIds.add(ideaNodeId)

      // Traverse up to find all ancestors
      const visited = new Set<string>()
      const queue = [ideaNodeId]

      while (queue.length > 0) {
        const currentId = queue.shift()
        if (currentId === undefined || visited.has(currentId)) continue
        visited.add(currentId)

        // Find parent edges (where current node is the target)
        const parentEdges = edges.filter((e) => e.target === currentId)
        for (const edge of parentEdges) {
          threadNodeIds.add(edge.source)
          threadEdges.push(edge as MindMapEdge)
          if (!visited.has(edge.source)) {
            queue.push(edge.source)
          }
        }
      }

      // Also include child ideas (ideas under this idea) for full context
      const childQueue = [ideaNodeId]
      const childVisited = new Set<string>([ideaNodeId])

      while (childQueue.length > 0) {
        const currentId = childQueue.shift()
        if (currentId === undefined) continue

        // Find child edges (where current node is the source)
        const childEdges = edges.filter((e) => e.source === currentId)
        for (const edge of childEdges) {
          const childNode = nodes.find((n) => n.id === edge.target)
          // Only include child ideas
          if (childNode && childNode.data.category === 'idea' && !childVisited.has(edge.target)) {
            threadNodeIds.add(edge.target)
            threadEdges.push(edge as MindMapEdge)
            childVisited.add(edge.target)
            childQueue.push(edge.target)
          }
        }
      }

      // Convert to MindMapNode format
      const threadNodes: MindMapNode[] = nodes
        .filter((n) => threadNodeIds.has(n.id))
        .map((node) => ({
          id: node.id,
          type: node.type,
          position: node.position,
          data: node.data,
        })) as MindMapNode[]

      return { nodes: threadNodes, edges: threadEdges }
    },
    [nodes, edges]
  )

  const generateFigmaPromptForIdea = useCallback(
    (ideaNodeId: string) => {
      if (!mindmapId) return

      // Check figma_prompt feature access
      const { hasAccess } = checkFeature('figma_prompt')
      if (!hasAccess) {
        setUpgradeFeatureName('UI prompt export')
        setShowUpgradeModal(true)
        return
      }

      const ideaNode = nodes.find((n) => n.id === ideaNodeId)
      if (!ideaNode || ideaNode.data.category !== 'idea') {
        antdNotification.error({
          message: 'Invalid node',
          description: 'UI prompt export is only available for idea nodes.',
          title: undefined,
        })
        return
      }

      // Gather the thread (idea + ancestors + child ideas)
      const { nodes: threadNodes, edges: threadEdges } = gatherThreadNodes(ideaNodeId)

      if (threadNodes.length === 0) {
        antdNotification.warning({
          message: 'No content to export',
          description: 'Could not find the idea thread.',
          title: undefined,
        })
        return
      }

      // Set state and open modal
      setFigmaThreadNodes(threadNodes)
      setFigmaThreadEdges(threadEdges)
      setFigmaModalOpen(true)
    },
    [mindmapId, nodes, gatherThreadNodes, antdNotification, checkFeature]
  )

  // Opens a lightweight confirmation modal — no AI calls yet
  const generateStoryboardForIdea = useCallback(
    async (ideaNodeId: string) => {
      if (!mindmapId) return

      const ideaNode = nodes.find((n) => n.id === ideaNodeId)
      if (!ideaNode || ideaNode.data.category !== 'idea') {
        antdNotification.error({
          message: 'Invalid node',
          description: 'Storyboard generation is only available for idea nodes.',
          title: undefined,
        })
        return
      }

      // Check storyboard quota before opening modal
      const { data: quota, error: quotaError } = await ProfileService.getStoryboardQuota()
      if (!quotaError && quota && !quota.canGenerate) {
        setUpgradeFeatureName('Unlimited storyboards')
        setShowUpgradeModal(true)
        return
      }

      setStoryboardIdeaLabel(ideaNode.data.label || 'Untitled Idea')
      setStoryboardIdeaNodeId(ideaNodeId)
      setStoryboardConfirmModalOpen(true)
    },
    [mindmapId, nodes, antdNotification]
  )

  const handleCloseFigmaModal = useCallback(() => {
    setFigmaModalOpen(false)
    setFigmaThreadNodes([])
    setFigmaThreadEdges([])
  }, [])

  const handleCloseStoryboardModal = useCallback(() => {
    setStoryboardModalOpen(false)
    setStoryboardIdeaNodeId('')
    setStoryboardExistingUrls(undefined)
    setStoryboardExistingCaptions(undefined)
    setStoryboardExistingTitle(undefined)
  }, [])

  const handleCloseConfirmModal = useCallback(() => {
    setStoryboardConfirmModalOpen(false)
    setStoryboardIdeaNodeId('')
    setStoryboardIdeaLabel('')
  }, [])

  // Called when user confirms in the confirmation modal.
  // Creates the node immediately, then chains both AI calls in the background.
  const handleConfirmNewStoryboard = useCallback(async () => {
    if (!storyboardIdeaNodeId || !mindmapId) return

    setStoryboardConfirmModalOpen(false)

    const ideaNode = nodes.find((n) => n.id === storyboardIdeaNodeId)
    if (!ideaNode) return

    const existingStoryboardCount = nodes.filter(
      (n) =>
        n.data.category === 'storyboard' &&
        (n.data as unknown as StoryboardNodeData).parentNodeId === storyboardIdeaNodeId
    ).length

    const storyboardNodeId = uuidv4()

    const newPosition = {
      x: ideaNode.position.x + 50 + existingStoryboardCount * 320,
      y: ideaNode.position.y + 200,
    }

    const newStoryboardNode = {
      id: storyboardNodeId,
      type: 'storyboardNode',
      position: newPosition,
      data: {
        id: storyboardNodeId,
        label: 'Generating storyboard...',
        category: 'storyboard' as const,
        mindmapId,
        storyboardTitle: undefined,
        storyboardUrls: [],
        parentLabel: storyboardIdeaLabel,
        parentNodeId: storyboardIdeaNodeId,
        isGenerating: true,
      },
    }

    const newEdge = {
      id: uuidv4(),
      source: storyboardIdeaNodeId,
      target: storyboardNodeId,
      type: 'default',
    }

    setNodes((currentNodes) => [...currentNodes, newStoryboardNode as (typeof currentNodes)[0]])
    setEdges((currentEdges) => [...currentEdges, newEdge])

    let lastErrorCode: string | undefined

    try {
      // Step 1: Generate narrative descriptions, scene bible, and cast
      const descResponse = await AIService.generateStoryboardDescriptions(
        mindmapId,
        storyboardIdeaNodeId
      )
      if (descResponse.error) {
        lastErrorCode = descResponse.error.code
        throw new Error(descResponse.error.message || 'Failed to generate storyboard descriptions')
      }
      if (!descResponse.data) {
        throw new Error('Failed to generate storyboard descriptions')
      }

      const {
        storyboardTitle,
        descriptions,
        captions,
        sceneBible,
        cast,
        panelSceneIndices,
        panelCharacters,
      } = descResponse.data

      // Step 2: Generate the 4 images using those descriptions
      const genResponse = await AIService.generateStoryboard(
        mindmapId,
        storyboardIdeaNodeId,
        descriptions,
        sceneBible,
        cast,
        panelSceneIndices,
        panelCharacters
      )
      if (genResponse.error) {
        lastErrorCode = genResponse.error.code
        throw new Error(genResponse.error.message || 'Failed to generate storyboard')
      }

      const storyboardUrls = genResponse.data?.storyboardUrls
      if (!storyboardUrls || storyboardUrls.length !== 4) {
        throw new Error('Invalid storyboard data returned')
      }

      setNodes((currentNodes) =>
        currentNodes.map((node) => {
          if (node.id === storyboardNodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                label: storyboardTitle,
                storyboardTitle,
                storyboardUrls,
                isGenerating: false,
                frameDescriptions: descriptions,
                frameCaptions: captions,
                sceneBible,
                cast,
                panelSceneIndices,
                panelCharacters,
              },
            }
          }
          return node
        })
      )

      setHasUnsavedChanges(true)
      antdNotification.success({
        message: 'Storyboard generated',
        description: 'Your 4-panel storyboard has been created successfully.',
        title: undefined,
      })
    } catch (error) {
      setNodes((currentNodes) =>
        currentNodes.map((node) => {
          if (node.id === storyboardNodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                isGenerating: false,
              },
            }
          }
          return node
        })
      )

      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      // Use captured error code instead of string matching for reliable limit detection
      const isLimitError = lastErrorCode === 'STORYBOARD_LIMIT_REACHED'

      if (isLimitError) {
        setUpgradeFeatureName('Unlimited storyboards')
        setShowUpgradeModal(true)
      } else {
        antdNotification.error({
          message: 'Failed to generate storyboard',
          description: errorMessage,
          title: undefined,
        })
      }
    }
  }, [
    storyboardIdeaNodeId,
    storyboardIdeaLabel,
    mindmapId,
    nodes,
    setNodes,
    setEdges,
    setHasUnsavedChanges,
    antdNotification,
  ])

  // View an existing storyboard node (opens modal in view-only mode)
  const viewStoryboardNode = useCallback(
    (storyboardNodeId: string) => {
      if (!mindmapId) return

      const storyboardNode = nodes.find((n) => n.id === storyboardNodeId)
      if (!storyboardNode || storyboardNode.data.category !== 'storyboard') {
        return
      }

      const storyboardData = storyboardNode.data as unknown as StoryboardNodeData
      const parentNodeId = storyboardData.parentNodeId
      if (!parentNodeId) return

      setStoryboardIdeaLabel(storyboardData.parentLabel || 'Untitled Idea')
      setStoryboardIdeaNodeId(parentNodeId)
      setStoryboardExistingUrls(storyboardData.storyboardUrls)
      setStoryboardExistingCaptions(storyboardData.frameCaptions)
      setStoryboardExistingTitle(storyboardData.storyboardTitle)
      setStoryboardModalOpen(true)
    },
    [mindmapId, nodes]
  )

  const contextValue = useMemo<IdeaActionsContextValue>(
    () => ({
      generateFigmaPromptForIdea,
      generateStoryboardForIdea,
      viewStoryboardNode,
      hasVentureAudienceForIdea,
    }),
    [
      generateFigmaPromptForIdea,
      generateStoryboardForIdea,
      viewStoryboardNode,
      hasVentureAudienceForIdea,
    ]
  )

  return (
    <IdeaActionsContext.Provider value={contextValue}>
      {children}

      <FigmaExportModal
        isOpen={figmaModalOpen}
        onClose={handleCloseFigmaModal}
        mindmapId={mindmapId ?? ''}
        threadNodes={figmaThreadNodes}
        threadEdges={figmaThreadEdges}
      />

      <StoryboardModal
        isOpen={storyboardModalOpen}
        onClose={handleCloseStoryboardModal}
        ideaLabel={storyboardIdeaLabel}
        existingUrls={storyboardExistingUrls}
        existingCaptions={storyboardExistingCaptions}
        existingTitle={storyboardExistingTitle}
      />

      <StoryboardConfirmModal
        isOpen={storyboardConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmNewStoryboard}
        ideaLabel={storyboardIdeaLabel}
      />

      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={upgradeFeatureName}
      />
    </IdeaActionsContext.Provider>
  )
}
